import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <header><Navbar /></header>
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-article-line text-2xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您访问的文章不存在或已被移除</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const pageTitle = `${article.title} - 爱液视频`;
  const pageDescription = article.excerpt;
  const pageKeywords = article.tags.join(',') + ',爱液视频';

  return (
    <>
      <ScrollToTop />
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalUrl={`https://www.aiyevideo.com/article/${article.id}`}
        ogType="article"
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogImage={article.cover}
        schemaData={[{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          image: article.cover,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          datePublished: article.date,
          publisher: {
            '@type': 'Organization',
            name: '爱液视频',
          },
        }]}
      />

      <header>
        <Navbar />
      </header>

      <main className="pt-16 bg-background-50">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <div className="w-full px-4 md:px-6 py-8 md:py-10">
          <div className="flex flex-col lg:flex-row gap-8 max-w-5xl mx-auto">
            {/* Main Content */}
            <article className="flex-1 min-w-0">
              {/* Cover Image */}
              <div className="aspect-[16/9] rounded-lg overflow-hidden mb-6">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Meta */}
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-time-line"></i> {article.readTime}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                </span>
              </div>

              {/* Title */}
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                {article.title}
              </h1>

              {/* Content */}
              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed
                  [&_h2]:font-heading [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                  [&_p]:mb-4 [&_p]:text-sm [&_p]:leading-relaxed
                  [&_strong]:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex items-center gap-2 mt-8 pt-6 border-t border-background-200/70 flex-wrap">
                <span className="text-xs text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:w-80 flex-shrink-0">
              <div className="sticky top-24">
                <h3 className="font-heading font-semibold text-foreground-950 mb-4 text-sm">
                  相关推荐
                </h3>
                <div className="space-y-4">
                  {relatedArticles.length > 0 ? (
                    relatedArticles.map((related) => (
                      <ArticleCard key={related.id} article={related} />
                    ))
                  ) : (
                    articles.filter((a) => a.id !== article.id).slice(0, 3).map((related) => (
                      <ArticleCard key={related.id} article={related} />
                    ))
                  )}
                </div>

                <div className="mt-6 p-4 rounded-lg bg-primary-50 border border-primary-100">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-vip-crown-line text-primary-600"></i>
                    <span className="text-sm font-semibold text-primary-700">会员专享</span>
                  </div>
                  <p className="text-xs text-primary-600/80 mb-3">
                    开通会员即可畅享海量正版高清视频内容，新用户注册即享7天免费体验
                  </p>
                  <Link
                    to="/category/remen"
                    className="block text-center bg-primary-500 hover:bg-primary-600 text-background-50 py-2 rounded-full text-xs font-medium transition-colors cursor-pointer"
                  >
                    立即开通
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}